import { useMemo } from 'react'

/** Hook to create curried functions to update properties. Example:

 * const updateHandler = useUpdateHandler(onUpdatePartialOrganisation)
 *
   <OrganisationNameInput
     onChange={updateHandler("organisationName")}
   />
 *
 * If a base property is received as the second argument
 * const updateHandler = useUpdateHandler(onUpdateOrganisation, organisation)
 * then the callback will receive TEntity instead of Partial<TEntity>
 * as it will merge the received property with the base object.
 */

export function useUpdateHandler<TEntity>(
  onUpdate: (partial: Partial<TEntity>) => void
): <K extends keyof TEntity>(key: K) => (value: TEntity[K]) => void
export function useUpdateHandler<TEntity>(
  onUpdate: (partial: TEntity) => void,
  base: TEntity
): <K extends keyof TEntity>(key: K) => (value: TEntity[K]) => void

export function useUpdateHandler<TEntity>(onUpdate: (partial: Partial<TEntity>) => void, base?: TEntity) {
  return useMemo(() => makePropertyUpdateHandler(onUpdate, base), [base, onUpdate])
}

function makePropertyUpdateHandler<TEntity>(onUpdate: (partial: Partial<TEntity>) => void, base?: TEntity) {
  return function<K extends keyof TEntity>(key: K) {
    return (value: TEntity[K]) => {
      const partial = {
        ...(base ? base : {}),
        [key]: value
      }
      onUpdate(partial as any)
    }
  }
}
